import React from 'react';
import {connect} from 'react-redux';
import axios from 'axios';
import PropTypes from 'prop-types';

import SideBar from '../components/SideBar';
import LocaleUtils from '../utils/LocaleUtils';

class Uploader extends React.Component {
    static propTypes = {
        active: PropTypes.bool,
        setCurrentTask: PropTypes.func
    };

    state = {
        allowedSchemas: [],
        loading: true,
        error: null,
        selectedSchema: '',
        selectedTable: '',
        availableTables: [],
        file: null,
        uploading: false,
        description: '',
        breadcrumbs: [],
        breadcrumbValues: [],
        references: {}
    };

    componentDidMount() {
        this.fetchPermissions();
    }

    fetchPermissions = () => {
        axios.get('api/v1/uploader/permissions')
            .then(res => {
                // We expect the backend to return the list of schema objects
                // If your backend returns { permissions: [...] }, change this to res.data.permissions
                const schemas = res.data || [];
                this.setState({ 
                    allowedSchemas: schemas, 
                    loading: false,
                    // If only one schema exists, auto-select it and its tables
                    selectedSchema: schemas.length === 1 ? schemas[0].schema : '',
                    availableTables: schemas.length === 1 ? schemas[0].tables : []
                });
            })
            .catch(err => {
                this.setState({ 
                    error: "Could not load permissions.", 
                    loading: false 
                });
            });
    }

    handleSchemaChange = (e) => {
        const schemaName = e.target.value;
        const schemaData = this.state.allowedSchemas.find(s => s.schema === schemaName);
        this.setState({
            selectedSchema: schemaName,
            availableTables: schemaData ? schemaData.tables : [],
            selectedTable: ''
        });
    }

    handleUpload = () => {
        const { file, selectedSchema, selectedTable, description, breadcrumblvl1, breadcrumblvl2 } = this.state;
        
        if (!file) {
            alert("Please select a file.");
            return;
        }

        if (file.size === 0) {
            alert("The selected file is empty (0 bytes). Upload cancelled.");
            return;
        }

        if (!selectedSchema || !selectedTable) {
            alert("Please select both a schema and a table.");
            return;
        }

        this.setState({ uploading: true, error: null });

        const formData = new FormData();
        formData.append('schema', selectedSchema);
        formData.append('table', selectedTable);
        formData.append('description', description || '');
        this.state.breadcrumbValues.forEach((val, index) => {
            formData.append(`breadcrumblvl${index + 1}`, val);
        });
        formData.append('file', file);

        axios.post('api/v1/uploader/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000 
        })
        .then(res => {
            alert("Success: " + (res.data.status || "File processed"));
            this.setState({ uploading: false, file: null });
        })
        .catch(err => {
            const errorMsg = err.response?.data?.error || err.message;
            console.error("Upload failed:", err);
            this.setState({ uploading: false, error: "Upload failed: " + errorMsg });
        });
    }

    render() {
        // --- FIXED: Pulling these from state so they are defined in the render scope ---
        const { loading, allowedSchemas, availableTables, selectedSchema, selectedTable, error, uploading } = this.state;

        if (loading) {
            return (
                <SideBar id="Uploader" side="left" width="30em" title="Uploader" icon="upload"
                    active={this.props.active} onClose={() => this.props.setCurrentTask(null)}>
                    <div role="body" style={{padding: "1em"}}>Loading permissions...</div>
                </SideBar>
            );
        }

        if (!this.props.username || allowedSchemas.length === 0) {
            return (
                <SideBar id="Uploader" side="left" width="30em" title="Uploader" icon="upload"
                    active={this.props.active} onClose={() => this.props.setCurrentTask(null)}>
                    <div role="body" style={{display: "flex", flexDirection: "column", padding: "1em"}}>
                        <p>Sorry, no upload permissions found for <strong>{this.props.username || "Guest"}</strong>.</p>
                        {error && <p style={{color: 'red'}}>{error}</p>}
                    </div>
                </SideBar>
            );
        }

        const activeSchemaObj = allowedSchemas.find(s => s.schema === selectedSchema);
        const breadcrumbLabels = activeSchemaObj?.breadcrumbs || [];
        const references = activeSchemaObj?.references || {};

        const breadcrumbInputs = breadcrumbLabels.map((label, index) => {
            let currentLevelOptions = references; // Start at the root of references

            // Traverse the tree based on previous selections
            for (let i = 0; i < index; i++) {
                const selectedValue = this.state.breadcrumbValues[i];
                
                // IMPORTANT: step into the specific selected name, then into its children
                if (currentLevelOptions && currentLevelOptions[selectedValue]) {
                    currentLevelOptions = currentLevelOptions[selectedValue].children;
                } else {
                    currentLevelOptions = {}; // Break if selection doesn't exist
                    break;
                }
            }
            
            // Now currentLevelOptions is the 'children' object of the previous selection
            const suggestions = currentLevelOptions ? Object.keys(currentLevelOptions) : [];
            const currentValue = this.state.breadcrumbValues[index] || "";

            return (
                <div key={index} style={{display: "flex", flexDirection: "column", marginTop: "1em"}}>
                    <label>{label}</label>
                    <select 
                        className="combo-config"
                        value={currentValue}
                        disabled={index > 0 && !this.state.breadcrumbValues[index - 1]}
                        onChange={(e) => {
                            const newValues = [...this.state.breadcrumbValues];
                            newValues[index] = e.target.value;
                            newValues.splice(index + 1); 
                            this.setState({ breadcrumbValues: newValues });
                        }}
                    >
                        <option value="">Select {label}...</option>
                        {suggestions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
            );
        });

        return (
            <SideBar id="Uploader" side="left" width="30em" title="Uploader" icon="upload"
                active={this.props.active} onClose={() => this.props.setCurrentTask(null)}>
                <div role="body" style={{display: "flex", flexDirection: "column", height: "100%", padding: "1em"}}>
                    
                    {error && <div style={{color: 'red', marginBottom: '1em'}}>{error}</div>}

                    <label>Schema</label>
                    <select className="combo-config" value={selectedSchema} onChange={this.handleSchemaChange}>
                        <option value="">Select a schema...</option>
                        {allowedSchemas.map(s => (
                            <option key={s.schema} value={s.schema}>{s.schema}</option>
                        ))}
                    </select>

                    <label style={{marginTop: "1em"}}>Table</label>
                    <select 
                        className="combo-config" 
                        value={selectedTable}
                        onChange={e => this.setState({selectedTable: e.target.value})}
                        disabled={!selectedSchema}
                    >
                        <option value="">Select a table...</option>
                        {availableTables.map(tableName => (
                            <option key={tableName} value={tableName}>{tableName}</option>
                        ))}
                    </select>

                    <label style={{marginTop: "1em"}}>Description (optional)</label>
                    <input type="text" 
                        value={this.state.description} 
                        onChange={e => this.setState({description: e.target.value})} 
                    />
                    
                    {breadcrumbInputs}
                    
                    <div style={{marginTop: "2em", border: "1px dashed #ccc", padding: "10px"}}>
                        <input type="file" 
                            onChange={e => this.setState({file: e.target.files[0]})} 
                        />
                        {this.state.file && (
                            <div style={{fontSize: '0.8em', marginTop: '5px'}}>
                                Ready to upload: <strong>{(this.state.file.size / 1024).toFixed(2)} KB</strong>
                            </div>
                        )}
                    </div>

                    <button 
                        style={{
                            marginTop: "2em", 
                            padding: "12px", 
                            backgroundColor: uploading ? "#ccc" : "#007bff", 
                            color: "white", 
                            border: "none", 
                            borderRadius: "4px",
                            fontWeight: "bold",
                            cursor: uploading ? "not-allowed" : "pointer"
                        }}
                        disabled={!this.state.file || !selectedSchema || !selectedTable || uploading}
                        onClick={this.handleUpload}>
                        {uploading ? "Streaming to internal server..." : "Start Upload"}
                    </button>
                </div>
            </SideBar>
        );
    }
}

const selector = (state) => ({
    username: state.localConfig ? state.localConfig.username : null,
});

export default connect(selector, {})(Uploader);